import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { NotificacionService } from '@core/services/notificacion.service';
import {
    PreguntasSeguridadModalComponent,
    PreguntasSeguridadModalData,
    PreguntasSeguridadModalResult
} from '@features/authentication/ui/preguntas-seguridad-modal/preguntas-seguridad-modal.component';
import { CodigosRespuestaSIAFPConstants } from '@shared/constants/codigos-respuesta-siafp-constants';
import { TypeAlertEnum } from '@shared/enums/type.alert.enum';
import { environment } from '@envs/environment';
import { Observable, of, timer } from 'rxjs';
import { catchError, switchMap, take } from 'rxjs/operators';
import { PreguntasSeguridadService } from './preguntas-seguridad.service';

/**
 * Gate del Flujo 3: al abrir una opcion SIAFP Web exige preguntas registradas.
 * Flujo 4: apertura desde perfil (edicion si ya tiene preguntas).
 */
@Injectable({
    providedIn: 'root'
})
export class PreguntasSeguridadGateService {

    private static readonly MSJ_REQUIERE_PREGUNTAS_SIAFP =
        'Se requieren las preguntas de seguridad para poder continuar a SIAFP.';
    /** Toast visible ~3 s; el modal abre al segundo 3 (junto al cierre del toast). */
    private static readonly DURACION_TOAST_MS = 3000;
    private static readonly DELAY_MODAL_MS = 3000;

    private readonly _preguntasService = inject(PreguntasSeguridadService);
    private readonly _matDialog = inject(MatDialog);
    private readonly _notificacionService = inject(NotificacionService);

    private _enProceso = false;
    private _completasEnSesion: boolean | null = null;

    /**
     * Verifica estado y, si faltan preguntas, ejecuta aviso + modal hasta guardar.
     *
     * @returns true si puede continuar a SIAFP Web
     */
    public asegurarPreguntasAntesDeAbrir(): Observable<boolean> {
        if (!environment.preguntasSeguridadHabilitadas) {
            return of(true);
        }
        if (this._completasEnSesion === true) {
            return of(true);
        }
        if (this._enProceso) {
            return of(false);
        }
        this._enProceso = true;
        return this._preguntasService.consultarEstado().pipe(
            take(1),
            catchError(() => {
                this._enProceso = false;
                this._notificacionService.open({
                    typeAlertEnum: TypeAlertEnum.ERROR,
                    label: 'No fue posible validar las preguntas de seguridad. Intente de nuevo.'
                });
                return of(null);
            }),
            switchMap((estado) => {
                if (estado === null) {
                    return of(false);
                }
                if (estado.codigoRespuesta !== CodigosRespuestaSIAFPConstants.RESPUESTA_EXITOSA) {
                    this._enProceso = false;
                    this._notificacionService.open({
                        typeAlertEnum: TypeAlertEnum.ERROR,
                        label: estado.descripcionRespuesta
                            || 'No fue posible validar las preguntas de seguridad.'
                    });
                    return of(false);
                }
                if (estado.tienePreguntas === true) {
                    this._completasEnSesion = true;
                    this._enProceso = false;
                    return of(true);
                }
                return this.cicloAvisoYModal();
            })
        );
    }

    /**
     * Invalida el cache de sesion (p. ej. logout).
     */
    public limpiarCacheSesion(): void {
        this._completasEnSesion = null;
        this._enProceso = false;
    }

    /**
     * Flujo 4: abre el modal desde el menu de perfil (sin reauth ni aviso obligatorio).
     * Si ya tiene preguntas, precarga y permite editar; si no, registro normal.
     */
    public abrirDesdePerfil(): void {
        if (!environment.preguntasSeguridadHabilitadas) {
            return;
        }
        if (this._enProceso) {
            return;
        }
        this._enProceso = true;
        this._preguntasService.consultarEstado().pipe(
            take(1),
            catchError(() => {
                this._notificacionService.open({
                    typeAlertEnum: TypeAlertEnum.ERROR,
                    label: 'No fue posible consultar tus preguntas de seguridad.'
                });
                return of(null);
            }),
            switchMap((estado) => {
                if (estado === null) {
                    this._enProceso = false;
                    return of(undefined);
                }
                if (estado.codigoRespuesta !== CodigosRespuestaSIAFPConstants.RESPUESTA_EXITOSA) {
                    this._enProceso = false;
                    this._notificacionService.open({
                        typeAlertEnum: TypeAlertEnum.ERROR,
                        label: estado.descripcionRespuesta
                            || 'No fue posible consultar tus preguntas de seguridad.'
                    });
                    return of(undefined);
                }
                const data: PreguntasSeguridadModalData =
                    estado.tienePreguntas === true && (estado.preguntas?.length ?? 0) > 0
                        ? {
                            modo: 'edicion',
                            preguntasIniciales: estado.preguntas,
                            cantidadRequerida: 3
                        }
                        : { modo: 'registro' };
                return this.abrirModal(data);
            })
        ).subscribe((result) => {
            this.procesarResultadoModal(result);
        });
    }

    /**
     * Toast informativo (3 s) y modal al segundo 3.
     * Si el usuario cierra el modal (clic fuera o escape), no se insiste
     * hasta que vuelva a elegir una opcion SIAFP.
     */
    private cicloAvisoYModal(): Observable<boolean> {
        this.mostrarAvisoInformativo();
        return timer(PreguntasSeguridadGateService.DELAY_MODAL_MS).pipe(
            take(1),
            switchMap(() => this.abrirModal({ modo: 'registro' })),
            switchMap((result) => of(this.procesarResultadoModal(result)))
        );
    }

    /**
     * Interpreta el cierre del modal: exito si ya guardo, o cancelacion.
     * El modal con reauth persiste internamente para no perder respuestas ante error.
     *
     * @param result cierre del dialog
     * @return true si las preguntas quedaron guardadas
     */
    private procesarResultadoModal(result: PreguntasSeguridadModalResult | undefined): boolean {
        this._enProceso = false;
        if (!result?.confirmed || result.guardado !== true) {
            return false;
        }
        this._completasEnSesion = true;
        this._notificacionService.mostrarNotificacionBasica(
            'Preguntas de seguridad guardadas correctamente.',
            TypeAlertEnum.SUCCESS,
            PreguntasSeguridadGateService.DURACION_TOAST_MS
        );
        return true;
    }

    /**
     * Toast informativo (arriba derecha, ~3 s) indicando que faltan preguntas.
     */
    private mostrarAvisoInformativo(): void {
        this._notificacionService.mostrarNotificacionBasica(
            PreguntasSeguridadGateService.MSJ_REQUIERE_PREGUNTAS_SIAFP,
            TypeAlertEnum.INFO,
            PreguntasSeguridadGateService.DURACION_TOAST_MS
        );
    }

    private abrirModal(
        data: PreguntasSeguridadModalData
    ): Observable<PreguntasSeguridadModalResult | undefined> {
        return this._matDialog.open(PreguntasSeguridadModalComponent, {
            width: '560px',
            maxWidth: '95vw',
            disableClose: false,
            panelClass: 'preguntas-seguridad-dialog',
            data
        }).afterClosed().pipe(take(1));
    }

}
